import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SectionTitle from '../components/shared/SectionTitle';
import { Button } from '../components/ui/button';
import { heroImages, teamMembers, labSteps } from '../data/mock';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section - Image avec texte */}
      <section className="relative h-[70vh] min-h-[500px]">
        <img
          src={heroImages.home}
          alt="Foulées Vertes - Auvergne"
          className="w-full h-full object-cover object-bottom"
          loading="eager"
        />
        {/* Overlay léger */}
        <div className="absolute inset-0 bg-black/20" />
        {/* Texte vertical - aligné avec le conteneur du header */}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <div className="flex flex-col gap-1">
              <span className="text-4xl md:text-5xl lg:text-6xl font-neuropolitical font-bold text-white tracking-wide drop-shadow-lg">RUNNING</span>
              <span className="text-4xl md:text-5xl lg:text-6xl font-neuropolitical font-bold text-white tracking-wide drop-shadow-lg">TRAIL</span>
              <span className="text-4xl md:text-5xl lg:text-6xl font-neuropolitical font-bold text-white tracking-wide drop-shadow-lg">TRIATHLON</span>
              <span className="text-4xl md:text-5xl lg:text-6xl font-neuropolitical font-bold text-white tracking-wide drop-shadow-lg">SWIMRUN</span>
              <span className="text-4xl md:text-5xl lg:text-6xl font-neuropolitical font-bold text-white tracking-wide drop-shadow-lg">RANDONNÉE</span>
            </div>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-neuropolitical font-bold text-gray-900 mt-2 mb-6">
                Parce que l'expérience ne vaut que si elle est partagée
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6 text-justify">
                Que vous soyez coureur débutant, joggeur aguerri ou athlète confirmé, notre équipe vous accompagne à chaque foulée.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6 text-justify">
                Pratiquants passionnés depuis de nombreuses années, notre leitmotiv est de partager cette expérience et de vous apporter les bons conseils pour courir plus loin, plus vite, et surtout avec plaisir.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8 text-justify">
                Nous sélectionnons ainsi avec soin les meilleurs équipements pour le running : chaussures, vêtements techniques, accessoires, nutrition sportive. Parce que chaque coureur est unique, notre équipe est là pour vous guider de manière personnalisée avec simplicité et bonne humeur !
              </p>
              <Link to="/equipe">
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white group">
                  Découvrir l'équipe
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="/assets/images/templier.webp"
                alt="L'équipe Foulées Vertes"
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute -bottom-6 -left-6 bg-emerald-700 text-white p-6 rounded-xl shadow-lg max-w-xs hidden md:block">
                <p className="font-semibold text-lg">25 ans d'expertise</p>
                <p className="text-emerald-100 text-sm mt-1">Au service des coureurs auvergnats</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lab Preview Section */}
      <section className="py-20 bg-emerald-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-300 font-medium text-sm uppercase tracking-wider">Une foulée d'avance</span>
              <h2 className="text-3xl md:text-4xl font-neuropolitical font-bold mt-2 mb-6">
                Chez Foulées Vertes, on vit running depuis plus de vingt-cinq ans !
              </h2>
              <p className="text-emerald-100 leading-relaxed mb-6 text-justify">
                Tout a commencé en 2000, quand Karim, passionné de course à pied et amoureux de nature, a voulu créer un lieu pour tous les coureurs : du joggeur du dimanche au trailer acharné.
              </p>
              <p className="text-emerald-100 leading-relaxed mb-6 text-justify">
                Porté par un besoin d'évasion (et une bonne dose de folie), il s'est lancé dans des défis hors normes : du Marathon des Sables à l'Ironman d'Embrun, en passant par le Tor des Géants et la diagonale des fous.
              </p>
              <p className="text-emerald-100 leading-relaxed mb-8 text-justify">
                Ici, pas de blabla marketing : seulement de bons conseils, du matos de qualité, et une équipe qui court vraiment (oui, même sous la pluie). On vous aide à trouver LA paire qui vous convient, on organise des sorties, on teste des nouveautés… bref, on partage la même passion que vous.
              </p>
              <Link to="/le-lab">
                <Button className="bg-white text-emerald-800 hover:bg-emerald-50 group">
                  Découvrir Le Lab
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="/assets/images/piste.webp"
                alt="Le Lab - Piste d'analyse de foulée"
                className="rounded-2xl shadow-2xl w-full"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <SectionTitle
            title="Prêt à trouver votre chaussure idéale ?"
            subtitle="Prenez rendez-vous pour une analyse de foulée personnalisée et repartez avec la paire parfaite."
          />
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/rendez-vous">
              <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white px-8">
                Prendre RDV
              </Button>
            </Link>
            <Link to="/chaussures">
              <Button size="lg" variant="outline" className="px-8">
                Voir les chaussures
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
